import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

const SendJobPanel = ({
  selectedContacts,
  validJobs,
  selectedJob,
  onSelectJob,
  emailSubject,
  onUpdateEmailSubject,
  emailBody,
  onUpdateEmailBody,
  onSendJob,
  onClose
}) => {
  const [isSending, setIsSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Update email subject when job selection changes
  useEffect(() => {
    if (selectedJob) {
      onUpdateEmailSubject(`Job Opportunity: ${selectedJob.title} at ${selectedJob.company}`);
    }
  }, [selectedJob, onUpdateEmailSubject]);
  
  // Update email body when job selection changes
  useEffect(() => {
    if (selectedJob) {
      const newBody = `Hi [Name],

We thought this job might interest you:

${selectedJob.title} at ${selectedJob.company}
Location: ${selectedJob.location.city}, ${selectedJob.location.state}

Job Description:
${selectedJob.description}

Skills: ${selectedJob.skills.join(', ')}

Let us know if you're interested!

Best regards,
The Recruitment Team`;

      onUpdateEmailBody(newBody);
    }
  }, [selectedJob, onUpdateEmailBody]);
  
  const handleSendJob = async () => {
    setIsSending(true);
    try {
      const count = await onSendJob();
      setSentCount(count);
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error sending emails:', error);
    } finally {
      setIsSending(false);
    }
  };
    return (    <Drawer
      anchor="right"
      open={selectedContacts.length > 0 && true} // Always show when contacts are selected
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          p: 2.5,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)'
        }
      }}
    >      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        pb: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" fontWeight="600">
          Send Job to Contacts
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'primary.lighter'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>      {showSuccess ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mt: 4
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 0 20px rgba(0, 200, 83, 0.4)'
            }}
          >
            <MailOutlineIcon sx={{ fontSize: 50, color: 'white' }} />
          </Box>
          <Typography variant="h5" gutterBottom fontWeight="600" color="success.dark">
            Email Sent Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" fontSize="18px">
            {sentCount} email{sentCount === 1 ? '' : 's'} sent to selected contacts
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 3, px: 4, py: 1 }}
            onClick={onClose}
          >
            Close
          </Button>
        </Box>
      ) : (<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ mb: 4 }}>          <Typography
            variant="body1"
            gutterBottom
            fontWeight="600"
            sx={{
              display: 'flex',
              alignItems: 'center',
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            Selected Contacts ({selectedContacts.length})
          </Typography>          <Box
            sx={{
              maxHeight: 250,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              mt: 1,
              bgcolor: '#fafafa'
            }}
          >            <List dense disablePadding>
              {selectedContacts.map(contact => (
                <ListItem 
                  key={contact.id} 
                  divider
                  sx={{ 
                    py: 0.75,
                    px: 1
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white',
                        width: 32,
                        height: 32,
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {contact.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight="500">{contact.name}</Typography>}
                    secondary={
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {contact.email}
                      </Typography>
                    }
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>          <Typography
            variant="body1"
            gutterBottom
            fontWeight="600"
            sx={{
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            Job Selection
          </Typography>
          <FormControl fullWidth required sx={{ mb: 3, mt: 2 }}>
            <InputLabel id="job-select-label">Select Job *</InputLabel>
            <Select
              labelId="job-select-label"
              value={selectedJob ? selectedJob.id : ''}
              label="Select Job *"
              onChange={(e) => {
                const job = validJobs.find(j => j.id === e.target.value);
                onSelectJob(job);
              }}
              sx={{
                borderRadius: 1,
                '&:hover': {
                  boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              {validJobs.map(job => (
                <MenuItem key={job.id} value={job.id}>
                  <strong>{job.title}</strong> - {job.company}
                </MenuItem>
              ))}
            </Select>
          </FormControl>            {selectedJob && (
            <Box sx={{ mt: 2 }}>              <Typography
                variant="body1"
                gutterBottom
                fontWeight="600"
                sx={{
                  pb: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 2
                }}
              >
                Email Content
              </Typography>
              <TextField
                fullWidth
                label="Email Subject"
                value={emailSubject}
                onChange={(e) => onUpdateEmailSubject(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.light',
                    },
                  }
                }}
              />

              <TextField
                fullWidth
                label="Email Body"
                multiline
                rows={10}
                value={emailBody}
                onChange={(e) => onUpdateEmailBody(e.target.value)}
                helperText="Use [Name] to personalize the email with the contact's name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.light',
                    },
                  }
                }}
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 'auto',
          pt: 2
        }}>
          <Button
            variant="outlined"
            onClick={() => {
              // Make sure to call onClose to update the parent component state
              onClose();
            }}
            sx={{
              px: 3,
              py: 0.75,
              textTransform: 'none',
              borderRadius: 0.5,
              minWidth: '100px',
              '&:hover': {
                backgroundColor: 'error.lighter',
                borderColor: 'error.light'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedJob || isSending}
            onClick={handleSendJob}
            sx={{
              px: 3,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 'medium',
              borderRadius: 0.5,
              minWidth: '120px',
              boxShadow: 1
            }}
          >
            {isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </Box>
      </Box>
      )}
    </Drawer>
  );
};

export default SendJobPanel;
